import type { NoiseHudSnapshot } from "../../../features/noise-extraction";
import { t } from "../../../data/strings";
import hudPresetTable from "../../../data/tables/ui/huds/noise-extraction.json";
import { getUiLayout } from "../../layout";
import type { HudShellHandle } from "../../layout/hud-shell";
import type { UiHudPreset } from "../../layout/types";
import { resultHtml, topHtml } from "./markup";

export type NoiseHudHandlers = {
  onRestart: () => void;
  onQuit: () => void;
};

const preset = hudPresetTable as UiHudPreset;

function alertLabel(snap: NoiseHudSnapshot): string {
  switch (snap.threatHud) {
    case "exposed":
      return t("noise.hud.alertExposed");
    case "chasing":
      return t("noise.hud.alertChase");
    default:
      return t("noise.hud.alertOff");
  }
}

function causeLabel(snap: NoiseHudSnapshot): string {
  return snap.threatHud === "exposed" ? t("noise.hud.causeLeave") : "";
}

export function mountNoiseExtractionHud(handlers: NoiseHudHandlers): {
  sync: (snap: NoiseHudSnapshot) => void;
  destroy: () => void;
} {
  const ui = getUiLayout();
  ui.sync();
  const shell: HudShellHandle = ui.mountShell(preset);

  const top = shell.region("top");
  top.style.pointerEvents = "auto";
  top.innerHTML = topHtml();

  const mid = shell.region("mid");
  mid.style.pointerEvents = "none";

  const bottom = shell.region("bottom");
  bottom.style.pointerEvents = "none";
  bottom.innerHTML = "";

  const modal = shell.region("modal");
  modal.hidden = true;
  modal.style.pointerEvents = "none";
  let lastModalPhase: string | null = null;

  top.querySelector("[data-ne-restart]")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onRestart();
  });
  top.querySelector("[data-ne-quit]")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onQuit();
  });

  const bindModal = () => {
    modal.querySelector("[data-ne-again]")?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      handlers.onRestart();
    });
    modal
      .querySelector("[data-ne-quit-modal]")
      ?.addEventListener("click", (ev) => {
        ev.stopPropagation();
        handlers.onQuit();
      });
  };

  return {
    sync(snap) {
      const valueEl = top.querySelector("[data-ne-value]");
      const alertEl = top.querySelector("[data-ne-alert]") as HTMLElement | null;
      const causeEl = top.querySelector("[data-ne-cause]");
      if (valueEl) {
        valueEl.textContent = t("noise.hud.value", {
          n: String(snap.carryingValue),
        });
      }
      if (alertEl) {
        alertEl.textContent = alertLabel(snap);
        alertEl.dataset.tier =
          snap.threatHud === "none" ? "0" : snap.threatHud === "chasing" ? "2" : "1";
        if (snap.threatHud !== "none") alertEl.classList.add("is-pulse");
        else alertEl.classList.remove("is-pulse");
      }
      if (causeEl) causeEl.textContent = causeLabel(snap);

      const ended = snap.phase === "escaped" || snap.phase === "caught";
      if (ended) {
        if (lastModalPhase !== snap.phase) {
          lastModalPhase = snap.phase;
          modal.hidden = false;
          modal.style.pointerEvents = "auto";
          modal.innerHTML = resultHtml(snap);
          bindModal();
        }
      } else {
        lastModalPhase = null;
        modal.hidden = true;
        modal.style.pointerEvents = "none";
        modal.innerHTML = "";
      }
    },
    destroy() {
      shell.destroy();
    },
  };
}
